import { useEffect, useState } from 'react'

/** 클러스터 다운로드 — 받을 파일명을 입력받는다.
 *
 * 기본값은 클러스터 이름이고 매번 고칠 수 있다. 첫 입력값을 기억하지 않는 이유는
 * downloads 테이블에 클러스터를 가리키는 컬럼이 없어 저장할 자리가 없고, ZIP 캐시
 * 키가 내용 기반이라 같은 산출물을 다른 이름으로 그냥 서빙할 수 있기 때문이다.
 */
export default function ClusterDownloadModal({
  isOpen, defaultName, fileCount, onClose, onSubmit, title = '클러스터 다운로드',
}: {
  isOpen: boolean
  defaultName: string
  fileCount: number
  onClose: () => void
  onSubmit: (filename: string) => Promise<void>
  /** 검색결과처럼 클러스터가 아닌 집합도 같은 모달을 쓴다. */
  title?: string
}) {
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setName(defaultName)
    setError(null); setNotice(null); setSubmitting(false)
  }, [isOpen, defaultName])

  if (!isOpen) return null

  const isValid = name.trim().length > 0

  const handleSubmit = async () => {
    if (!isValid || submitting) return
    setError(null); setNotice(null)
    setSubmitting(true)
    try {
      await onSubmit(name.trim())
    } catch (e: any) {
      setError(e?.message || '다운로드에 실패했습니다.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[210]">
      <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
        <div className="text-lg font-semibold mb-2">{title}</div>
        <div className="text-xs text-gray-500 mb-4">
          이미지 {fileCount.toLocaleString()}장과 파일별 metadata JSON 을 ZIP 으로 받습니다.
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm text-gray-800 mb-1">
              파일명 <span className="text-red-500 ml-0.5">*</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                maxLength={200}
                disabled={submitting}
                autoFocus
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
              <span className="shrink-0 text-sm text-gray-500">.zip</span>
            </div>
          </div>
          {error && <div className="text-sm text-red-600">{error}</div>}
          {notice && <div className="text-sm text-gray-600">{notice}</div>}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50"
            onClick={onClose}
            disabled={submitting}
          >취소</button>
          <button
            className={`px-3 py-1 text-sm rounded text-white ${
              isValid && !submitting ? 'bg-gray-800 hover:bg-gray-900' : 'bg-gray-400 cursor-not-allowed'
            }`}
            disabled={!isValid || submitting}
            onClick={handleSubmit}
          >
            {submitting ? '준비 중...' : '다운로드'}
          </button>
        </div>
      </div>
    </div>
  )
}
